import { Suspense, lazy, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import {
  Box,
  Button,
  Divider,
  TableBody,
  Typography,
  withStyles,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";

import TableCellActions from "../../../Components/TableCellActions";
import PrimaryButton from "../../../Components/UI/Buttons/PrimaryButton";
import FormLayout from "../../../Components/UI/FormLayout";
import {
  StyledTable,
  StyledTableCell,
  StyledTableHead,
  StyledTableRow,
} from "../../../Components/UI/Tables/Table";

import {
  deletePlan,
  getPlan,
  toggleDelete,
  toggleItemModal,
  toggleOutcomeModal,
} from "../../../Stores/Survey/actions";

import DeleteModal from "../../../Components/Dialogs/DeleteModal";
import TableLoader from "../../../Components/UI/TableLoader";
import flexStyles from "../../../style/flex";
import { useStyles } from "../style";

const AddItem = lazy(() => import("./AddItem"));
const AddOutcome = lazy(() => import("./AddOutcome"));

const ActionButton = withStyles({
  root: {
    "&:hover": { background: "transparent" },
    padding: "0px 0px",
    minWidth: "0px",
  },
})(Button);

const ViewAction = () => {
  const dispatch = useDispatch();

  const flexClasses = flexStyles();
  const classes = useStyles();

  const { id } = useParams();
  const { currentPlan, deleteModal, isDeletingPlan, isGettingPlan } =
    useSelector((state) => state.SurveyReducer);

  let loading = isDeletingPlan || isGettingPlan;

  useEffect(() => {
    dispatch(getPlan(id));
  }, [id]);

  return (
    <>
      <FormLayout
        title={"Action Plan"}
        mode={"Edit"}
        pageName={"Action Plan"}
        parentLink={"Action Plan"}
        hideHeader={false}
        showLocationToggle={false}
      >
        <Box className={`${flexClasses.flexCenterEnd} ${classes.actionHeader}`}>
          <PrimaryButton
            hideIcon={true}
            onClick={() => dispatch(toggleItemModal({ open: true, id }))}
          >
            Create Plan
          </PrimaryButton>
        </Box>
        <Box>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell width="20%">You Said</StyledTableCell>
                <StyledTableCell width="20%">Outcome Expected</StyledTableCell>
                <StyledTableCell width="15%">By whom</StyledTableCell>
                <StyledTableCell width="15%">Target Date</StyledTableCell>
                <StyledTableCell width="30%">We did</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {loading ? (
                <TableLoader numOfColumns={6} />
              ) : (
                <>
                  {currentPlan?.survey_plan_items?.length > 0 ? (
                    currentPlan.survey_plan_items.map((row) => (
                      <>
                        <StyledTableRow>
                          <StyledTableCell>
                            <Typography>
                              {row?.item_description ?? `~`}
                            </Typography>
                          </StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                          <StyledTableCell>
                            <Box display="flex">
                              <Box display="flex">
                                <ActionButton
                                  disableRipple
                                  color="primary"
                                  onClick={() =>
                                    dispatch(
                                      toggleOutcomeModal({
                                        open: true,
                                        id: row?.id,
                                      }),
                                    )
                                  }
                                >
                                  <AddCircleOutlineIcon />
                                </ActionButton>
                                <Divider orientation="vertical" />
                              </Box>
                              <TableCellActions
                                canEdit={true}
                                canDelete={true}
                                canView={false}
                                editMethod={() =>
                                  dispatch(
                                    toggleItemModal({
                                      open: true,
                                      id,
                                      isEditing: true,
                                      currentItem: row,
                                    }),
                                  )
                                }
                                deleteMethod={() =>
                                  dispatch(
                                    toggleDelete({
                                      open: true,
                                      type: "item",
                                      id: row?.id,
                                      title: <>Delete Plan</>,
                                      description: (
                                        <>
                                          Performing this action will delete
                                          plan.
                                        </>
                                      ),
                                    }),
                                  )
                                }
                              />
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                        {row?.outcome_details?.length > 0 &&
                          row.outcome_details.map((outcome, j) => (
                            <StyledTableRow key={j}>
                              <StyledTableCell rowSpan={1}></StyledTableCell>
                              <StyledTableCell>
                                <Typography>
                                  {outcome?.expected_outcome ?? `~`}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Typography>
                                  {outcome?.assigned_to ?? `~`}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Typography>
                                  {outcome?.target_date_or_period ?? `~`}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <Typography>
                                  {outcome?.action_for_outcome ?? `~`}
                                </Typography>
                              </StyledTableCell>
                              <StyledTableCell>
                                <TableCellActions
                                  canEdit={true}
                                  canDelete={true}
                                  canView={false}
                                  editMethod={() =>
                                    dispatch(
                                      toggleOutcomeModal({
                                        open: true,
                                        currentOutcome: outcome,
                                        isEditing: true,
                                      }),
                                    )
                                  }
                                  deleteMethod={() =>
                                    dispatch(
                                      toggleDelete({
                                        open: true,
                                        type: "outcome",
                                        id: outcome?.id,
                                        title: <>Delete Outcome</>,
                                        description: (
                                          <>
                                            Performing this action will delete
                                            outcome
                                          </>
                                        ),
                                      }),
                                    )
                                  }
                                />
                              </StyledTableCell>
                            </StyledTableRow>
                          ))}
                      </>
                    ))
                  ) : (
                    <StyledTableRow>
                      <StyledTableCell
                        colSpan={6}
                        style={{ textAlign: "center" }}
                      >
                        <i>No Data Found!</i>
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </>
              )}
            </TableBody>
          </StyledTable>
        </Box>
      </FormLayout>
      <Suspense fallback={null}>
        <AddItem surveyId={id} />
        <AddOutcome surveyId={id} />
        <DeleteModal
          open={deleteModal.open}
          onClose={() => dispatch(toggleDelete({ open: false }))}
          details={deleteModal}
          isLoading={false}
          deleteMethod={() => {
            dispatch(
              deletePlan({
                id: deleteModal?.id,
                survey_id: id,
                type: deleteModal?.type,
              }),
            );
            dispatch(toggleDelete({ open: false }));
          }}
        />
      </Suspense>
    </>
  );
};

export default ViewAction;
